// # interfaces
import { iStorageService } from "@/interfaces/services/storage";

export default abstract class StorageService<iData>
  implements iStorageService<iData>
{
  /**
   * The key of the store where data is held.
   *
   * @var {string}
   */
  public abstract readonly storeName: string;

  /**
   * The storage API.
   *
   * @var {Storage}
   */
  protected storage: Storage;

  constructor(storage: Storage = window.localStorage) {
    this.storage = storage;
  }

  /**
   * Gets data from localStorage and parses to JSON
   *
   * @return {Promise<iData|null>}
   */
  public async get(): Promise<iData | null> {
    try {
      if (!this.storeName) {
        throw TypeError("No storeName set on this instance of LocalStorage");
      }

      const data = this.storage.getItem(this.storeName);

      if (data) {
        const model = JSON.parse(data);
        return Promise.resolve(model);
      }

      return this.defaultStoreValue;
    } catch (err) {
      return Promise.reject(err);
    }
  }

  /**
   * Sets data to localStorage
   *
   * @param {iData} model
   * @returns {Promise<null>}
   */
  public async set(model: iData): Promise<void> {
    try {
      if (!this.storeName) {
        throw TypeError("No storeName set on this instance of LocalStorage");
      }

      this.storage.setItem(this.storeName, JSON.stringify(model));

      return Promise.resolve();
    } catch (err) {
      return Promise.reject(err);
    }
  }

  /**
   * Clears this store
   *
   * @returns {Promise<void>}
   */
  public async clear(): Promise<void> {
    try {
      window.localStorage.removeItem(this.storeName);
      return Promise.resolve();
    } catch (err) {
      return Promise.reject(err);
    }
  }

  /**
   * The default value of the store.
   *
   * @returns {iData}
   */
  protected get defaultStoreValue(): iData {
    return null;
  }
}
